import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

// Same-origin: nginx (prod) and the Vite dev proxy both route /api to the
// backend. Absolute http:// URLs would be blocked as mixed content over HTTPS.
const API_URL = window.location.origin;

// Async thunks
export const fetchDataSets = createAsyncThunk(
	"datasets/fetchAll",
	async ({ workspaceId, projectId }) => {
		const params = {};
		if (workspaceId) params.workspaceId = workspaceId;
		if (projectId) params.projectId = projectId;

		const response = await api.get(`${API_URL}/api/datasets`, { params });
		return response.data;
	},
);

export const createDataSet = createAsyncThunk(
	"datasets/create",
	async (dataSet) => {
		const response = await api.post(`${API_URL}/api/datasets`, dataSet);
		return response.data;
	},
);

export const updateDataSet = createAsyncThunk(
	"datasets/update",
	async ({ id, dataSet }) => {
		const response = await api.put(
			`${API_URL}/api/datasets/${id}`,
			dataSet,
		);
		return response.data;
	},
);

export const deleteDataSet = createAsyncThunk("datasets/delete", async (id) => {
	await api.delete(`${API_URL}/api/datasets/${id}`);
	return id;
});

export const importCSV = createAsyncThunk(
	"datasets/importCSV",
	async ({ file, workspaceId, projectId, userId }) => {
		const formData = new FormData();
		formData.append("file", file);
		formData.append("workspaceId", workspaceId);
		formData.append("userId", userId);
		if (projectId) formData.append("projectId", projectId);

		const response = await api.post(
			`${API_URL}/api/datasets/import/csv`,
			formData,
			{
				headers: {
					"Content-Type": "multipart/form-data",
				},
			},
		);
		return response.data;
	},
);

export const exportCSV = createAsyncThunk("datasets/exportCSV", async (id) => {
	const response = await api.get(`${API_URL}/api/datasets/${id}/export/csv`, {
		responseType: "blob",
	});
	return response.data;
});

export const linkTestToDataSet = createAsyncThunk(
	"datasets/linkTest",
	async ({ dataSetId, testId }) => {
		await api.post(`${API_URL}/api/datasets/${dataSetId}/link/${testId}`);
		return { dataSetId, testId };
	},
);

export const unlinkTestFromDataSet = createAsyncThunk(
	"datasets/unlinkTest",
	async ({ dataSetId, testId }) => {
		await api.delete(`${API_URL}/api/datasets/${dataSetId}/link/${testId}`);
		return { dataSetId, testId };
	},
);

// Slice
const dataSetSlice = createSlice({
	name: "datasets",
	initialState: {
		dataSets: [],
		selectedDataSet: null,
		loading: false,
		error: null,
	},
	reducers: {
		selectDataSet: (state, action) => {
			state.selectedDataSet = action.payload;
		},
		clearSelectedDataSet: (state) => {
			state.selectedDataSet = null;
		},
		clearError: (state) => {
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			// Fetch DataSets
			.addCase(fetchDataSets.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchDataSets.fulfilled, (state, action) => {
				state.loading = false;
				state.dataSets = action.payload;
			})
			.addCase(fetchDataSets.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message;
			})

			// Create DataSet
			.addCase(createDataSet.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(createDataSet.fulfilled, (state, action) => {
				state.loading = false;
				state.dataSets.push(action.payload);
			})
			.addCase(createDataSet.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message;
			})

			// Update DataSet
			.addCase(updateDataSet.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updateDataSet.fulfilled, (state, action) => {
				state.loading = false;
				const index = state.dataSets.findIndex(
					(ds) => ds.id === action.payload.id,
				);
				if (index !== -1) {
					state.dataSets[index] = action.payload;
				}
			})
			.addCase(updateDataSet.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message;
			})

			// Delete DataSet
			.addCase(deleteDataSet.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(deleteDataSet.fulfilled, (state, action) => {
				state.loading = false;
				state.dataSets = state.dataSets.filter(
					(ds) => ds.id !== action.payload,
				);
			})
			.addCase(deleteDataSet.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message;
			})

			// Import CSV
			.addCase(importCSV.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(importCSV.fulfilled, (state, action) => {
				state.loading = false;
				state.dataSets.push(action.payload);
			})
			.addCase(importCSV.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message;
			})

			// Link Test
			.addCase(linkTestToDataSet.fulfilled, (state, action) => {
				const dataSet = state.dataSets.find(
					(ds) => ds.id === action.payload.dataSetId,
				);
				if (dataSet) {
					if (
						!dataSet.linkedTestIds.includes(action.payload.testId)
					) {
						dataSet.linkedTestIds.push(action.payload.testId);
						dataSet.usageCount += 1;
					}
				}
			})

			// Unlink Test
			.addCase(unlinkTestFromDataSet.fulfilled, (state, action) => {
				const dataSet = state.dataSets.find(
					(ds) => ds.id === action.payload.dataSetId,
				);
				if (dataSet) {
					dataSet.linkedTestIds = dataSet.linkedTestIds.filter(
						(testId) => testId !== action.payload.testId,
					);
					dataSet.usageCount = Math.max(0, dataSet.usageCount - 1);
				}
			});
	},
});

export const { selectDataSet, clearSelectedDataSet, clearError } =
	dataSetSlice.actions;
export default dataSetSlice.reducer;
